/** Purchases — **S3-03**, ADR-0044. A register of what was bought, and why none of it has a figure.
 *
 * ## What this screen is for, and the thing it will not do
 *
 * It records a finance ledger and asks the questions each line raises. It **never shows an
 * emission, a multiplier or a price** — the API returns none, and the generated types have nowhere
 * to put one — because no spend line can be priced in either market today (ADR-0044's acceptance
 * note; T-27) and a number here would be a number with no receipt (rule 3).
 *
 * So every line says what it is missing, in four kinds that ask for four different things:
 *
 * - **Needs your answer** — a fact only the customer holds and the line does not state: what the
 *   amount *is*, its VAT, its classification code, whether it is capital. The form is on the line.
 * - **Left out** — correctly not a priced purchase (depreciation, a lease, a credit). Nothing to
 *   fix here; the export is what changes.
 * - **Cannot be priced** — the loaded sets cannot price it as it stands, whatever the customer
 *   says: no set for the jurisdiction, no multiplier for that year, a different currency.
 * - **No path to a figure yet** — the one reason that is about EmitIQ rather than the line: a
 *   published multiplier exists and the product cannot turn it into a figure with a receipt.
 *
 * ## Nothing is looked up, and nothing is suggested
 *
 * The vendor is printed **as the file stated it**, and linking it to a register entry is a choice
 * the reader makes from the whole list. There is no "did you mean" — a suggestion is a lookup, and
 * the lookup is the change SEC-05 forbids. The same goes for the classification: a code is typed,
 * and the server says whether the loaded set publishes it.
 *
 * ## Roles
 *
 * Mirrored from `api/procurement.py`. Answering a line and linking a vendor are collection work;
 * recording a ledger moves a document to `VERIFIED`, which only a person in an Approver's seat can
 * do. A role that cannot is told who can, never shown a button that predictably 403s (design
 * rule 6).
 *
 * ## What is not here
 *
 * **No way to correct an imported amount**, and no decision history — only the latest answer and
 * who gave it are kept. **No upload control**: a ledger is an ordinary document, uploaded on the
 * Evidence screen, and chosen here.
 */

import { useState } from "react";

import { useDocuments } from "../api/evidence";
import type { ProcurementLine, SpendAbsence } from "../api/procurement";
import {
  useAcceptProcurement,
  useDecideLine,
  useLinkVendor,
  useProcurementLines,
  useProcurementPreview,
} from "../api/procurement";
import { useEntities } from "../api/queries";
import { useRegisters } from "../api/sources";
import { Loaded, ReadFailed, WriteFailed } from "../components/Loaded";
import { Panel } from "../components/Panel";
import { shortDate, statedQuantity } from "../lib/format";
import styles from "./Screen.module.css";
import own from "./Procurement.module.css";

const KIND_LABEL: Record<SpendAbsence["kind"], string> = {
  question: "Needs your answer",
  left_out: "Left out",
  not_priceable: "Cannot be priced",
  no_figure_path: "No path to a figure yet",
};

/** The order the kinds are listed in: what the reader can act on first. */
const KIND_ORDER: readonly SpendAbsence["kind"][] = [
  "question",
  "left_out",
  "not_priceable",
  "no_figure_path",
];

const BASIS_LABEL: Record<string, string> = {
  acquisition: "a purchase",
  depreciation: "a depreciation charge",
  recurring: "a lease or subscription",
  not_stated: "not stated",
};

export function ProcurementPanels({
  mayCollect,
  mayDecide,
}: {
  /** `undefined` while `/auth/me` is in flight — which is not "may not". */
  mayCollect: boolean | undefined;
  mayDecide: boolean | undefined;
}) {
  const lines = useProcurementLines(null);
  const registers = useRegisters();
  // `Loaded` handles an empty array; the response is an object, so hand it the array and keep the
  // note that travels with it for outside.
  const asList = {
    data: lines.data?.lines,
    isPending: lines.isPending,
    error: lines.error,
    refetch: () => void lines.refetch(),
  };

  return (
    <>
      <Panel
        title="Purchases"
        sub="What was bought, as the ledger stated it — and what each line is missing"
        column="1 / 9"
      >
        {lines.data ? (
          <p className={styles.note} data-testid="procurement-limits">
            {lines.data.limits_of_this_register}
          </p>
        ) : null}
        {/* The register only supplies the name of the vendor a person linked a line to. A failed read
            is not "no vendor is registered": say so, rather than let a linked line read as if its
            vendor were unknown. */}
        {registers.error ? (
          <p className={styles.note} role="status" data-testid="procurement-registers-failed">
            The vendor register could not be read, so a line linked to a vendor is shown without the
            vendor&rsquo;s name.{" "}
            <button type="button" className={styles.fixLink} onClick={() => void registers.refetch()}>
              Try again
            </button>
          </p>
        ) : null}
        <Loaded
          query={asList}
          of="The purchase lines"
          empty={
            <>
              No purchase ledger has been recorded. Upload a finance export on Evidence, then choose
              it here. Spend-based estimation is an initial assessment and its publishers recommend
              activity data where you have it — which is why the supplier requests above come first.
            </>
          }
        >
          {(rows) => (
            <ul className={own.list} data-testid="procurement-lines">
              {rows.map((line) => (
                <LineCard
                  key={line.id}
                  line={line}
                  vendors={registers.data?.vendors ?? null}
                  mayCollect={mayCollect}
                />
              ))}
            </ul>
          )}
        </Loaded>
      </Panel>

      <Panel title="Record a ledger" sub="From a file already uploaded" column="9 / 13">
        <ImportLedger mayDecide={mayDecide} />
      </Panel>
    </>
  );
}

// ------------------------------------------------------------------------------------ a line

function LineCard({
  line,
  vendors,
  mayCollect,
}: {
  line: ProcurementLine;
  vendors: ReadonlyArray<{ id: string; name: string }> | null;
  mayCollect: boolean | undefined;
}) {
  const [editing, setEditing] = useState(false);
  const linked = vendors?.find((vendor) => vendor.id === line.vendor_id);
  const byKind = KIND_ORDER.map((kind) => ({
    kind,
    items: line.assessment.absences.filter((absence) => absence.kind === kind),
  })).filter((group) => group.items.length > 0);

  return (
    <li className={own.line} data-testid="procurement-line">
      <span className={own.head}>
        {/* **As stated.** The file's own string, never a register entry's name: a link is shown
            beside it and does not replace it. */}
        <span className={own.vendor}>{line.vendor_name}</span>
        <span className={own.amount}>
          {statedQuantity(line.amount)} {line.currency}
        </span>
      </span>
      <span className={own.meta}>
        {shortDate(line.acquired_on)} · {BASIS_LABEL[line.basis] ?? line.basis} · VAT{" "}
        {line.vat.replace(/_/g, " ")}
        {line.reference ? ` · ${line.reference}` : ""}
        {line.classification && line.classification_code
          ? ` · ${line.classification} ${line.classification_code}`
          : " · no classification"}
        {line.is_capital === null ? "" : line.is_capital ? " · capital" : " · not capital"}
      </span>
      {line.description ? <span className={own.description}>{line.description}</span> : null}
      {line.vendor_id ? (
        <span className={own.meta} data-testid="vendor-linked">
          Linked to {linked ? linked.name : "a vendor in the register"} by a person.
        </span>
      ) : null}

      {/* The statement of absence, in every line and never omitted. The word is "No figure", not a
          dash or a zero: a dash reads as not-yet-loaded and a zero reads as a measurement. */}
      <span className={own.nofigure} data-testid="no-figure">
        No figure
      </span>
      <ul className={own.reasons}>
        {byKind.map((group) => (
          <li key={group.kind} data-kind={group.kind} className={own.group}>
            <strong>{KIND_LABEL[group.kind]}</strong>
            <ul className={own.items}>
              {group.items.map((absence) => (
                <li key={absence.code} data-code={absence.code}>
                  {absence.message}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      {line.assessment.published ? (
        <span className={own.published} data-testid="published-note">
          {line.assessment.published.publisher} publishes a multiplier for{" "}
          {line.assessment.published.code} ({line.assessment.published.category_label}),{" "}
          {line.assessment.published.multiplier_year}, in {line.assessment.published.currency}.{" "}
          {line.assessment.published.caveat}
        </span>
      ) : null}

      {mayCollect === false ? (
        <p className={own.unauthorized} data-testid="not-permitted-answer">
          <strong>A Contributor, Approver or Admin answers a line.</strong> You can read what each
          one is missing.
        </p>
      ) : (
        <>
          <button
            type="button"
            className={styles.fixLink}
            data-testid="answer-line"
            onClick={() => setEditing((was) => !was)}
          >
            {editing ? "Close" : "Answer what is missing, or link a vendor"}
          </button>
          {editing ? <Answers line={line} vendors={vendors} /> : null}
        </>
      )}
    </li>
  );
}

function Answers({
  line,
  vendors,
}: {
  line: ProcurementLine;
  vendors: ReadonlyArray<{ id: string; name: string }> | null;
}) {
  const decide = useDecideLine(line.id);
  const link = useLinkVendor(line.id);
  const [basis, setBasis] = useState(line.basis === "not_stated" ? "" : line.basis);
  const [vat, setVat] = useState(line.vat === "not_stated" ? "" : line.vat);
  const [scheme, setScheme] = useState(line.classification ?? "");
  const [code, setCode] = useState(line.classification_code ?? "");
  const [capital, setCapital] = useState(
    line.is_capital === null ? "" : line.is_capital ? "yes" : "no",
  );
  const [factorType, setFactorType] = useState(line.factor_type ?? "");
  const [reason, setReason] = useState(line.factor_type_reason ?? "");
  const [vendorId, setVendorId] = useState(line.vendor_id ?? "");
  const [refusal, setRefusal] = useState<unknown>(null);
  const [linkRefusal, setLinkRefusal] = useState<unknown>(null);

  return (
    <span className={own.form} data-testid="answers">
      <label className={styles.field}>
        <span>What is this amount?</span>
        <select value={basis} onChange={(e) => setBasis(e.target.value)}>
          <option value="">Not stated</option>
          <option value="acquisition">A purchase, in the year it was acquired</option>
          <option value="depreciation">A depreciation charge</option>
          <option value="recurring">A lease or subscription payment</option>
        </select>
      </label>
      <label className={styles.field}>
        <span>Does it include VAT?</span>
        <select value={vat} onChange={(e) => setVat(e.target.value)}>
          <option value="">Not stated</option>
          <option value="net">No — net of VAT</option>
          <option value="gross">Yes — gross</option>
        </select>
      </label>
      <label className={styles.field}>
        <span>Classification scheme</span>
        <input value={scheme} onChange={(e) => setScheme(e.target.value)} placeholder="The scheme the code is from" />
      </label>
      <label className={styles.field}>
        <span>Classification code</span>
        <input value={code} onChange={(e) => setCode(e.target.value)} />
      </label>
      <label className={styles.field}>
        <span>Used to make what you sell?</span>
        <select value={capital} onChange={(e) => setCapital(e.target.value)}>
          <option value="">Not stated</option>
          <option value="yes">Yes — capital goods (category 2)</option>
          <option value="no">No — goes into what you sell (category 1)</option>
        </select>
      </label>
      <label className={styles.field}>
        <span>Factor type, where the publisher offers several</span>
        <select value={factorType} onChange={(e) => setFactorType(e.target.value)}>
          <option value="">Not stated</option>
          <option value="without_margins">Without margins</option>
          <option value="margins">Margins</option>
          <option value="with_margins">With margins</option>
        </select>
      </label>
      {factorType ? (
        <label className={styles.field}>
          <span>Why that one?</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      ) : null}
      <button
        type="button"
        className={styles.primary}
        data-testid="save-answers"
        disabled={decide.isPending}
        onClick={() => {
          setRefusal(null);
          decide
            .mutateAsync({
              basis,
              vat,
              classification: scheme,
              classification_code: code,
              category: "",
              capital,
              factor_type: factorType,
              factor_type_reason: factorType ? reason : "",
            })
            .catch((failure: unknown) => setRefusal(failure));
        }}
      >
        {decide.isPending ? "Saving…" : "Save these answers"}
      </button>
      {/* An empty field is recorded as *not stated*, and the sentence says so: the form replaces
          the whole set, and a reader who clears a box should know it is a statement. */}
      <p className={styles.note}>
        An empty answer is saved as <em>not stated</em>. Nothing here is guessed for you.
      </p>
      <WriteFailed error={refusal} of="this line" />

      {vendors === null ? (
        <p className={styles.note}>The vendor register could not be read, so no link is offered.</p>
      ) : vendors.length === 0 ? (
        <p className={styles.note}>
          The vendor register is empty. Add a vendor on Sources, then link it here.
        </p>
      ) : (
        <>
          <label className={styles.field}>
            <span>Link to a vendor you have recorded</span>
            <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <option value="">No link</option>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={styles.fixLink}
            data-testid="link-vendor"
            disabled={link.isPending}
            onClick={() => {
              setLinkRefusal(null);
              link
                .mutateAsync(vendorId === "" ? null : vendorId)
                .catch((failure: unknown) => setLinkRefusal(failure));
            }}
          >
            {vendorId === "" ? "Remove the link" : "Link it"}
          </button>
          <WriteFailed error={linkRefusal} of="this line" />
        </>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------------- the import

function ImportLedger({ mayDecide }: { mayDecide: boolean | undefined }) {
  const entities = useEntities();
  const documents = useDocuments();
  const [entityId, setEntityId] = useState("");
  const [documentId, setDocumentId] = useState("");
  const preview = useProcurementPreview(documentId || null, entityId || null);
  const accept = useAcceptProcurement();
  const [refusal, setRefusal] = useState<unknown>(null);
  const [done, setDone] = useState<{ lines: number; already: boolean } | null>(null);

  // Spreadsheets that have not been accepted. A verified one has produced lines or figures, and
  // offering it would invite a second import the server answers with "already recorded".
  const candidates = (documents.data?.rows ?? []).filter(
    (row) => row.kind === "spreadsheet" && ["validated", "awaiting_review"].includes(row.state),
  );

  return (
    <span className={own.form}>
      <Loaded
        query={entities}
        of="The list of your companies"
        empty="No company is set up yet. Add one on Organisation first."
      >
        {(rows) => (
          <label className={styles.field}>
            {/* **Chosen, never read from the file.** The company decides the jurisdiction and so
                which publisher's set could ever price a line. */}
            <span>Whose books is this?</span>
            <select
              value={entityId}
              onChange={(e) => {
                setEntityId(e.target.value);
                setDone(null);
              }}
            >
              <option value="">Choose a company…</option>
              {rows.map((entity) => (
                <option key={entity.id} value={entity.id}>
                  {entity.legal_name}
                </option>
              ))}
            </select>
          </label>
        )}
      </Loaded>

      {documents.error ? (
        <ReadFailed
          of="The list of your files"
          error={documents.error}
          onRetry={() => void documents.refetch()}
        />
      ) : (
        <label className={styles.field}>
          <span>Which file?</span>
          <select
            value={documentId}
            onChange={(e) => {
              setDocumentId(e.target.value);
              setDone(null);
            }}
          >
            <option value="">
              {candidates.length === 0 ? "No spreadsheet is waiting" : "Choose a file…"}
            </option>
            {candidates.map((row) => (
              <option key={row.id} value={row.id}>
                {row.original_filename ?? row.id}
              </option>
            ))}
          </select>
        </label>
      )}

      {documentId && entityId ? (
        <Loaded query={preview} of="The preview of that file">
          {(read) => (
            <span className={own.preview} data-testid="ledger-preview">
              <strong>
                {read.rows.length} line{read.rows.length === 1 ? "" : "s"} read
                {read.importable ? "" : " — none can be recorded yet"}
              </strong>
              {read.errors.length > 0 ? (
                <ul className={own.problems} data-testid="ledger-errors">
                  {/* **Everything wrong with it**, up to a screenful: a file's problems are
                      usually one problem repeated. */}
                  {read.errors.slice(0, 12).map((problem) => (
                    <li key={`${problem.line}-${problem.column ?? "row"}`}>
                      Line {problem.line}
                      {problem.column ? `, ${problem.column}` : ""}: {problem.message}
                    </li>
                  ))}
                </ul>
              ) : null}
              {read.notes.map((note) => (
                <span key={note} className={own.meta}>
                  {note}
                </span>
              ))}
              {/* What the file did not say, as counts of absences — so the reader knows how many
                  questions are coming before they accept. */}
              <span className={own.meta} data-testid="ledger-unstated">
                The file does not say what the amount is on {read.unstated.basis}, whether it
                includes VAT on {read.unstated.vat}, or a classification on{" "}
                {read.unstated.classification}.
              </span>
              {read.possible_repeats.length > 0 ? (
                <span className={own.repeat} data-testid="ledger-repeats">
                  <strong>Possibly recorded already.</strong> Line{" "}
                  {read.possible_repeats.join(", ")} look like purchases already recorded from
                  another file. Nothing is refused for that — two identical purchases are ordinary —
                  but an overlapping export would count a month twice.
                </span>
              ) : null}
              {mayDecide === false ? (
                <p className={own.unauthorized} data-testid="not-permitted-record">
                  <strong>An Approver records a ledger.</strong> It moves the file to verified,
                  which only a person in that seat can do.
                </p>
              ) : (
                <button
                  type="button"
                  className={styles.primary}
                  data-testid="record-ledger"
                  disabled={!read.importable || accept.isPending}
                  onClick={() => {
                    setRefusal(null);
                    accept
                      .mutateAsync({ documentId, entityId })
                      .then((result) =>
                        setDone({ lines: result.lines, already: result.already_imported }),
                      )
                      .catch((failure: unknown) => setRefusal(failure));
                  }}
                >
                  {accept.isPending ? "Recording…" : `Record ${read.rows.length} lines`}
                </button>
              )}
            </span>
          )}
        </Loaded>
      ) : null}

      {done ? (
        <p className={styles.note} data-testid="ledger-recorded">
          {done.already
            ? `Already recorded — ${done.lines} lines. Nothing was written this time.`
            : `Recorded ${done.lines} lines. None has a figure; each says what it is missing.`}
        </p>
      ) : null}
      {/* A record that may or may not have landed is answered by the server if it is sent again,
          but the sentence still sends the reader to look: they should see the lines. */}
      <WriteFailed error={refusal} of="the Purchases list" />
    </span>
  );
}
